import React, { useState, useEffect } from 'react';
import { getGlossary } from '../utils/api';
import { toast } from 'sonner';
import { BookOpen, Info } from 'lucide-react';

const GlossaryPage = () => {
  const [glossary, setGlossary] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGlossary();
  }, []);

  const fetchGlossary = async () => {
    try {
      const data = await getGlossary();
      setGlossary(data);
    } catch (error) {
      toast.error('Failed to load glossary');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-600">Loading glossary...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="glossary-page" className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-2">Medical Glossary</h1>
        <p className="text-lg leading-relaxed text-slate-700">Plain-language explanations of common blood markers</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
        <div className="flex items-start">
          <Info className="h-6 w-6 text-blue-600 mt-1 mr-3 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-blue-800 mb-2">Educational Purpose Only</p>
            <p className="text-sm text-blue-700 leading-relaxed">
              This glossary is for educational purposes only and should not be used for self-diagnosis. 
              Always consult your healthcare provider to interpret your test results.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {glossary.map((item, index) => (
          <div
            key={index}
            className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-xl">
                <BookOpen className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.term}</h3>
                <p className="text-slate-700 leading-relaxed mb-3">{item.definition}</p>
                {item.normal_range && (
                  <div className="inline-block px-4 py-2 bg-slate-100 rounded-lg">
                    <p className="text-sm text-slate-600">
                      <span className="font-medium">Normal Range:</span> {item.normal_range}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-100">
        <h3 className="text-lg font-semibold text-slate-900 mb-3">Understanding Your Results</h3>
        <p className="text-slate-700 leading-relaxed mb-4">
          Blood test results can vary based on many factors including time of day, recent meals, medications, 
          and individual health conditions. What's "normal" can also vary by age, sex, and laboratory standards.
        </p>
        <p className="text-slate-700 leading-relaxed">
          <span className="font-medium">Important:</span> Never make treatment decisions based solely on blood test numbers. 
          Your healthcare team considers your complete medical history, physical examination, symptoms, and other factors 
          when providing care recommendations.
        </p>
      </div>
    </div>
  );
};

export default GlossaryPage;